/**
 * OrderPrintTemplate - Shared print/PDF template component
 *
 * This component renders a print-friendly version of the order that works
 * identically in both frontend (offline-first) and WP-Admin backend contexts.
 */

import React from 'react';
import type { OrderPayload, UIText, Currency } from './types';
import { formatPrice, getEventCurrency, getImageUrl } from './utils';

interface OrderPrintTemplateProps {
	order: OrderPayload;
	eventName?: string;
	orderId?: string;
	timestamp?: string;
	translations?: UIText;
	title?: string;
	email?: string; // Contact email from shortcode/backend
}

export const OrderPrintTemplate = React.forwardRef<
	HTMLDivElement,
	OrderPrintTemplateProps
>( ( { order, eventName = 'EXPODENTAL', orderId, timestamp, translations, title, email }, ref ) => {
	// Extract print translations from the full UIText object
	const printTranslations = translations?.print || translations || {};
	const t = printTranslations;

	// Use provided email or fall back to translation
	const displayEmail = email || t.email || 'orders.eu@sprintray.com';

	// Normalize order data - handle undefined properties from PHP backend
	// Note: PHP backend may use 'appliedPromotions' instead of 'promotions'
	const normalizedOrder = {
		...order,
		cartItems: order.cartItems || [],
		promotions: order.promotions || order.appliedPromotions || [],
		freeResins: order.freeResins || [],
		customerInfo: order.customerInfo || {},
	};

	// Format timestamp for display
	const formatTimestamp = ( isoString: string ) => {
		const date = new Date( isoString );
		// Determine locale based on translations website URL
		const locale = t.website?.includes('/de-') ? 'de-DE' :
		               t.website?.includes('/es-') ? 'es-ES' : 'en-GB';
		return date.toLocaleString( locale, {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
		} );
	};

	// Determine number format locale (kept for non-price number formatting)
	const getNumberLocale = () => {
		if (t.website?.includes('/de-')) return 'de-DE';
		if (t.website?.includes('/es-')) return 'es-ES';
		return 'en-GB';
	};

	const numberLocale = getNumberLocale();

	// Get currency from order items or default to event currency
	const orderCurrency: Currency = (() => {
		// Try to get currency from cart items
		if (normalizedOrder.cartItems.length > 0 && normalizedOrder.cartItems[0].product.currency) {
			return normalizedOrder.cartItems[0].product.currency;
		}
		// Try to get currency from promotions
		if (normalizedOrder.promotions.length > 0 && normalizedOrder.promotions[0].currency) {
			return normalizedOrder.promotions[0].currency;
		}
		// Default to event currency
		return getEventCurrency();
	})();

	// Calculate totals
	const cartTotal = normalizedOrder.cartItems.reduce(
		( sum, item ) => sum + item.product.price * item.quantity,
		0
	);
	const promotionsTotal = normalizedOrder.promotions.reduce(
		( sum, promo ) => sum + promo.promoPrice,
		0
	);
	const freeResinsTotal = normalizedOrder.freeResins.reduce(
		( sum, resin ) => sum + ( resin.value || 0 ),
		0
	);

	return (
		<div
			id="sprintray-print-container"
			ref={ ref }
			className="sprintray-print-container"
		>
			{ /* Header Section */ }
			<header className="spr-print-header">
				<div className="spr-print-header__main">
					<div className="spr-print-header__left">
						<img
							src={ getImageUrl( 'assets/images/logo/SprintRayLogo-Original.png' ) }
							alt="SprintRay"
							className="spr-print-header__logo"
						/>
						<div className="spr-print-header__title">
							<h1>{ title || t.title || 'QUOTE REQUEST FORM' }</h1>
							<p>{ eventName }</p>
						</div>
					</div>
					<div className="spr-print-header__right">
						<span>{ t.website || 'www.sprintray.com' }</span>
						<span>{ displayEmail }</span>
					</div>
				</div>
			</header>

			{ /* Customer Information */ }
			<section className="spr-print-section">
				<div className="spr-print-section-header">
					<h2 className="spr-print-section__title">{ t.customerData || 'Customer Information' }</h2>
					{ normalizedOrder.customerInfo.requestType && (
						<div className="spr-print-request-type">
							<span className="spr-print-request-type-label">{ t.requestType || 'Type of Request' }:</span>
							<span className="spr-print-request-type-value">
								{ normalizedOrder.customerInfo.requestType === 'order'
									? ( t.requestTypeOrder || 'Order' )
									: order.customerInfo.requestType === 'lead'
									? ( t.requestTypeLead || 'Lead' )
									: ( t.requestTypeQuote || 'Quote Request' ) }
							</span>
						</div>
					) }
				</div>
				<div className="spr-print-grid">
					<div className="spr-print-field spr-print-field--full">
						<label>{ t.nombreClinicaLaboratorio || 'Clinic / Laboratory' }</label>
						<span>
							{ normalizedOrder.customerInfo.nombreClinicaLaboratorio ||
								'-' }
						</span>
					</div>
					<div className="spr-print-field">
						<label>{ t.nombre || 'First Name' }</label>
						<span>
							{ normalizedOrder.customerInfo.nombre }{ ' ' }
							{ normalizedOrder.customerInfo.apellidos }
						</span>
					</div>
					<div className="spr-print-field">
						<label>{ t.email || 'Email' }</label>
						<span>{ normalizedOrder.customerInfo.email || '-' }</span>
					</div>
					<div className="spr-print-field">
						<label>{ t.telefono || 'Phone' }</label>
						<span>{ normalizedOrder.customerInfo.telefono || '-' }</span>
					</div>
					<div className="spr-print-field spr-print-field--full">
						<label>{ t.direccion || 'Address' }</label>
						<span>
							{ normalizedOrder.customerInfo.direccion },{ ' ' }
							{ normalizedOrder.customerInfo.codigoPostal }{ ' ' }
							{ normalizedOrder.customerInfo.ciudad }
							{ normalizedOrder.customerInfo.pais &&
								` - ${ normalizedOrder.customerInfo.pais }` }
						</span>
					</div>
				</div>
			</section>

			{ /* Trade Partner */ }
			<section className="spr-print-section">
				<h2 className="spr-print-section__title">{ t.tradePartner || 'Trade Partner' }</h2>
				<div className="spr-print-grid">
					<div className="spr-print-field">
						<label>{ t.distribuidor || 'Distributor' }</label>
						<span>{ normalizedOrder.customerInfo.distribuidor || '-' }</span>
					</div>
					{ normalizedOrder.customerInfo.nombreFachberater && (
						<div className="spr-print-field">
							<label>{ t.nombreFachberater || 'Advisor' }</label>
							<span>{ normalizedOrder.customerInfo.nombreFachberater }</span>
						</div>
					) }
				</div>
			</section>

			{ /* Products */ }
			{ normalizedOrder.cartItems.length > 0 && (
				<section className="spr-print-section">
					<h2 className="spr-print-section__title">{ t.productos || 'Products' }</h2>
					<table className="spr-print-table">
						<thead>
							<tr>
								<th>{ t.producto || 'Product' }</th>
								<th>{ t.variante || 'Var.' }</th>
								<th>{ t.cant || 'Qty' }</th>
								<th className="spr-print-table--right">
									{ t.precio || 'Price' }
								</th>
								<th className="spr-print-table--right">
									{ t.total || 'Total' }
								</th>
							</tr>
						</thead>
						<tbody>
							{ normalizedOrder.cartItems.map( ( item, index ) => (
								<tr key={ `cart-${ index }` }>
									<td>
										<strong>{ item.product.name }</strong>
										{ item.selectedVariant && (
											<small>
												{ ' ' }
												( { item.selectedVariant } )
											</small>
										) }
									</td>
									<td>{ item.selectedVariant || '-' }</td>
									<td className="spr-print-table--center">
										{ item.quantity }
									</td>
									<td className="spr-print-table--right">
										{ formatPrice(
											item.product.price,
											item.product.currency || orderCurrency
										) }
									</td>
									<td className="spr-print-table--right">
										<strong>
											{ formatPrice(
												item.product.price * item.quantity,
												item.product.currency || orderCurrency
											) }
										</strong>
									</td>
								</tr>
							) ) }
							{ cartTotal > 0 && (
								<tr className="spr-print-table__subtotal">
									<td
										colSpan={ 4 }
										className="spr-print-table--right"
									>
										<strong>{ t.subtotalProductos || 'Subtotal Products:' }</strong>
									</td>
									<td className="spr-print-table--right">
										<strong>
											{ formatPrice( cartTotal, orderCurrency ) }
										</strong>
									</td>
								</tr>
							) }
						</tbody>
					</table>
				</section>
			) }

			{ /* Promotions */ }
			{ normalizedOrder.promotions.length > 0 && (
				<section className="spr-print-section">
					<h2 className="spr-print-section__title">{ t.promociones || 'Promotions' }</h2>
					<table className="spr-print-table">
						<thead>
							<tr>
								<th>{ t.promocion || 'Promotion' }</th>
								<th>{ t.incluye || 'Includes' }</th>
								<th className="spr-print-table--right">
									{ t.precio || 'Price' }
								</th>
							</tr>
						</thead>
						<tbody>
							{ normalizedOrder.promotions.map( ( promo ) => (
								<tr key={ promo.id }>
									<td>
										<strong>{ promo.title }</strong>
									</td>
									<td>
										<small>{ promo.description }</small>
										<br />
										<span className="spr-print-tag">
											{ promo.includes?.join( ' + ' ) ||
												'-' }
										</span>
									</td>
									<td className="spr-print-table--right">
										<strong>
											{ formatPrice( promo.promoPrice, promo.currency || orderCurrency ) }
										</strong>
									</td>
								</tr>
							) ) }
							{ promotionsTotal > 0 && (
								<tr className="spr-print-table__subtotal">
									<td
										colSpan={ 2 }
										className="spr-print-table--right"
									>
										<strong>{ t.subtotalPromociones || 'Subtotal Promotions:' }</strong>
									</td>
									<td className="spr-print-table--right">
										<strong>
											{ formatPrice( promotionsTotal, orderCurrency ) }
										</strong>
									</td>
								</tr>
							) }
						</tbody>
					</table>
				</section>
			) }

			{ /* Free Resins */ }
			{ normalizedOrder.freeResins.length > 0 && (
				<section className="spr-print-section">
					<h2 className="spr-print-section__title">
						{ t.resinasGratuitas || 'Free Resins' }
					</h2>
					<table className="spr-print-table">
						<thead>
							<tr>
								<th>{ t.resina || 'Resin' }</th>
								<th>{ t.variante || 'Var.' }</th>
								<th className="spr-print-table--center">
									{ t.cant || 'Qty' }
								</th>
								<th className="spr-print-table--right">
									{ t.precio || 'Price' }
								</th>
							</tr>
						</thead>
						<tbody>
							{ Object.entries(
								normalizedOrder.freeResins.reduce(
									( acc, resin ) => {
										// Group by both resinId AND variant
										const key = `${ resin.resinId }-${ resin.variant || 'default' }`;
										if ( ! acc[ key ] ) {
											acc[ key ] = {
												name: resin.name,
												variant: resin.variant,
												value: resin.value,
												quantity: 0,
											};
										}
										acc[ key ].quantity++;
										return acc;
									},
									{} as Record<
										string,
										{
											name: string;
											variant?: string;
											value: number;
											quantity: number;
										}
									>
								)
							).map(
								( [ key, { name, variant, value, quantity } ] ) => (
									<tr key={ `free-resin-${ key }` }>
										<td>
											<strong>{ name }</strong>
											{ variant && (
												<small>
													{ ' ' }
													({ variant })
												</small>
											) }
										</td>
										<td>{ variant || '-' }</td>
										<td className="spr-print-table--center">
											{ quantity }
										</td>
										<td className="spr-print-table--right">
											<span className="spr-print-strike">
												{ formatPrice( value || 0, orderCurrency ) }
											</span>
											<span className="spr-print-free">
												{ formatPrice( 0, orderCurrency ) }
											</span>
										</td>
									</tr>
								)
							) }
							<tr className="spr-print-table__subtotal">
								<td
									colSpan={ 2 }
									className="spr-print-table--right"
								>
									<strong>{ t.subtotalResinasGratuitas || 'Subtotal Free Resins:' }</strong>
								</td>
								<td className="spr-print-table--right">
									<strong>{ formatPrice( 0, orderCurrency ) }</strong>
								</td>
							</tr>
						</tbody>
					</table>
				</section>
			) }

			{ /* Selected Package (SprintRace Advantage) */ }
			{ normalizedOrder.selectedPackage && (
				<section className="spr-print-section">
					<h2 className="spr-print-section__title">
						{ t.selectedPackage || 'Selected Package' }
					</h2>
					<table className="spr-print-table">
						<thead>
							<tr>
								<th>{ t.producto || 'Product' }</th>
								<th>{ t.variante || 'Var.' }</th>
								<th className="spr-print-table--center">{ t.cant || 'Qty' }</th>
								<th className="spr-print-table--right">
									{ t.precio || 'Price' }
								</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>
									<strong>{ normalizedOrder.selectedPackage.name }</strong>
								</td>
								<td>
									{ normalizedOrder.selectedPackage.selectedVariants
										? Object.values( normalizedOrder.selectedPackage.selectedVariants )
												.filter( Boolean )
												.join( ', ' ) || '-'
										: '-' }
								</td>
								<td className="spr-print-table--center">1</td>
								<td className="spr-print-table--right">
									<strong>
										{ formatPrice( normalizedOrder.selectedPackage.value, orderCurrency ) }
									</strong>
								</td>
							</tr>
							<tr className="spr-print-table__subtotal">
								<td
									colSpan={ 3 }
									className="spr-print-table--right"
								>
									<strong>{ t.subtotalPackage || 'Package Total:' }</strong>
								</td>
								<td className="spr-print-table--right">
									<strong>
										{ formatPrice( normalizedOrder.selectedPackage.value, orderCurrency ) }
									</strong>
								</td>
							</tr>
						</tbody>
					</table>
				</section>
			) }

			{ /* Remarks */ }
			{ normalizedOrder.remarks && (
				<section className="spr-print-section">
					<h2 className="spr-print-section__title">{ t.observaciones || 'Remarks' }</h2>
					<p className="spr-print-remarks">{ normalizedOrder.remarks }</p>
				</section>
			) }

			{ /* Grand Total Row - hidden for lead mode */ }
			{ order.customerInfo.requestType !== 'lead' && (
				<table className="spr-print-table spr-print-grand-total">
				<colgroup>
					<col style={ { width: '1fr' } } />
					<col style={ { width: '120px' } } />
				</colgroup>
				<tbody>
					<tr className="spr-print-table__total">
						<td className="spr-print-table--right">
							<strong>{ t.totalPedido || 'ORDER TOTAL' }</strong>
						</td>
						<td className="spr-print-table--right">
							{ formatPrice( normalizedOrder.totalAmount, orderCurrency ) }
						</td>
					</tr>
				</tbody>
			</table>
			) }

			{ /* Footer with Signature and Meta */ }
			<footer className="spr-print-footer">
				<div className="spr-print-bottom">
					<div className="spr-print-signature">
						{ normalizedOrder.signature ? (
							<img
								src={ normalizedOrder.signature }
								alt={ t.firma || 'Signature' }
								className="spr-print-signature-img"
								style={ {
									borderBlock: '2px solid black !important',
									width: '50% !important',
								} }
							/>
						) : (
							<div className="spr-print-signature-placeholder">
								{ t.firmaDelCliente || 'Customer Signature' }
							</div>
						) }
					</div>

					<div className="spr-print-meta">
						<p>
							<strong>{ t.fechaPedido || 'Order Date:' }</strong>{ ' ' }
							{ formatTimestamp( normalizedOrder.timestamp ) }
						</p>
						{ orderId && (
							<p>
								<strong>{ t.id || 'ID:' } </strong> { orderId }
							</p>
						) }
						{ timestamp && (
							<p>
								<strong>{ t.registrado || 'Registered:' }</strong>{ ' ' }
								{ formatTimestamp( timestamp ) }
							</p>
						) }
					</div>
				</div>
			</footer>

			{ /* Disclaimer */ }
			<div className="spr-print-disclaimer">
				{ t.disclaimer ? (
					<>
						{ t.disclaimer.split('\n\n').map( ( paragraph, index ) => (
							<p key={ index }>{ paragraph }</p>
						) ) }
					</>
				) : (
					<>
						<p>All prices are in euros and exclude VAT.</p>
						<p>
							Once the system is purchased through your distributor,
							send an email to{ ' ' }
							<a href={`mailto:${displayEmail}`}>
								{ displayEmail }
							</a>{ ' ' }
							with proof of payment or invoice to receive the free
							resins.
						</p>
					</>
				) }
			</div>
		</div>
	);
} );

OrderPrintTemplate.displayName = 'OrderPrintTemplate';

export default OrderPrintTemplate;
